import React from 'react';
import { graphql, useFragment } from 'react-relay';

import type { PaymentActivePixCobValue_charge$key } from './__generated__/PaymentActivePixCobValue_charge.graphql.ts';
import type { PaymentActivePixCobValue_pixQrCode$key } from './__generated__/PaymentActivePixCobValue_pixQrCode.graphql.ts';
import { useChargePaymentMethod } from '../../../charge/useChargePaymentMethod.tsx';
import PaymentLinkActiveValue from '../paymentLinkActive/PaymentLinkActiveValue.tsx';
import PaymentLinkValueFirstInstallment from '../paymentLinkActive/PaymentLinkValueFirstInstallment.tsx';

import type { ReactNode } from 'react';

type PaymentActivePixCobValueProps = {
  charge: PaymentActivePixCobValue_charge$key;
  pixQrCode: PaymentActivePixCobValue_pixQrCode$key;
};
const PaymentActivePixCobValue = (
  props: PaymentActivePixCobValueProps,
): ReactNode => {
  const charge = useFragment<PaymentActivePixCobValue_charge$key>(
    graphql`
      fragment PaymentActivePixCobValue_charge on Charge {
        id
        value
        valueWithDiscount
        company {
          ...PaymentLinkActiveValue_company
        }
        decodedBrCode {
          interests
          fines
          finalValue
        }
        chargeIntent {
          id
        }
        installment: paymentSubscriptionInstallment {
          installmentNumber
        }
        paymentMethods {
          ... on PaymentMethodPix {
            value
            valueWithDiscount
          }
        }
        ...PaymentLinkValueFirstInstallment_charge
        ...useChargePaymentMethod_charge
      }
    `,
    props.charge,
  );

  const pixQrCode = useFragment<PaymentActivePixCobValue_pixQrCode$key>(
    graphql`
      fragment PaymentActivePixCobValue_pixQrCode on PixQrCode {
        company {
          ...PaymentLinkActiveValue_company
        }
        value
      }
    `,
    props.pixQrCode,
  );

  const { pixCob } = useChargePaymentMethod(charge);

  if (pixQrCode) {
    return (
      <PaymentLinkActiveValue
        company={pixQrCode.company}
        value={pixQrCode.value}
      />
    );
  }

  if (
    charge?.chargeIntent?.id &&
    charge?.installment?.installmentNumber === 1
  ) {
    return <PaymentLinkValueFirstInstallment charge={charge} />;
  }

  const getFinalValue = () => {
    const { finalValue } = charge?.decodedBrCode || {};

    if (finalValue) {
      return finalValue;
    }

    if (pixCob.valueWithDiscount) {
      return pixCob.valueWithDiscount;
    }

    return charge.paymentMethods?.[0]?.value;
  };

  const finalValue = getFinalValue();

  return (
    <PaymentLinkActiveValue
      company={charge.company}
      value={pixCob.value}
      finalValue={finalValue}
      valueWithDiscount={pixCob.valueWithDiscount}
    />
  );
};

export default PaymentActivePixCobValue;
